/**
 * T018 - US1: React Query hook for fetching provider's authored resources
 * Feature 011 - Service Provider Profile Improvements
 *
 * Fetches resources authored/contributed by a service provider
 * Filters out deleted resources
 * Sorts by publishedAt DESC (newest first)
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../core/api/client';

interface ResourceType {
  id: string;
  name: string;
}

interface ResourceImage {
  id: string;
  fileUrl: string;
  formats?: Record<string, unknown>;
}

export interface Resource {
  id: string;
  title: string;
  excerpt: string;
  seoUrl: string;
  type: ResourceType;
  image: ResourceImage | null;
  publishedAt: string;
  deletedAt?: string | null;
}

interface ResourcesResponse {
  data: Resource[];
  meta: {
    total: number;
    page: number;
    limit: number;
  };
}

/**
 * Fetch resources authored by a specific service provider
 *
 * @param providerId - Service provider UUID
 * @param enabled - Whether to enable the query (default: true)
 * @returns React Query result with resources data
 */
export const useProviderResources = (providerId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['provider-resources', providerId],
    queryFn: async (): Promise<Resource[]> => {
      if (!providerId) {
        return [];
      }

      const response = await apiClient.get<ResourcesResponse>('/resources', {
        params: {
          authorProId: providerId,
          sort: 'newest', // Newest first (T013) - matches ResourceSortOption.NEWEST
          limit: 100, // Show all without pagination (clarification Q2)
        },
      });

      // Filter out deleted resources (T015)
      const resources = response.data.data || [];
      return resources.filter(resource => !resource.deletedAt);
    },
    enabled: enabled && !!providerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
